// Applies the visitor's cookie-banner choice to the tags loaded in index.html.
// index.html sets Google Consent Mode defaults (denied in the EEA/UK/CH, granted
// elsewhere) and re-applies a stored choice on every page load; this pushes the
// choice the moment the banner is answered, so "Decline" actually stops tracking.

export type ConsentChoice = "accepted" | "declined";

export const CONSENT_NOTICE_VERSION = "2026-10-16-google-cookieless-v1";
export const COOKIE_SETTINGS_EVENT = "loftloom:cookie-settings";
export const CONSENT_CHANGED_EVENT = "loftloom:consent-changed";
export const CONSENT_KEY = "loftloom_cookie_consent";

export function consentSignals(choice: ConsentChoice) {
  return choice === "accepted" ? GRANTED : DENIED;
}

export function recordConsent(choice: ConsentChoice) {
  const history = JSON.parse(localStorage.getItem("loftloom_consent_history") || "[]") as unknown[];
  let visitor = localStorage.getItem("loftloom_consent_visitor");
  if (!visitor) {
    visitor = crypto.randomUUID();
    localStorage.setItem("loftloom_consent_visitor", visitor);
  }
  localStorage.setItem("loftloom_consent_history", JSON.stringify([...history, {
    visitor, choice, at: new Date().toISOString(), notice: CONSENT_NOTICE_VERSION,
    region: "unknown", recipient: "Google", purpose: "advertising measurement and optimization",
  }]));
  localStorage.setItem(CONSENT_KEY, choice);
  applyConsent(choice);
}

const GRANTED = {
  ad_storage: "granted",
  ad_user_data: "granted",
  ad_personalization: "granted",
  analytics_storage: "granted",
} as const;

const DENIED = {
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
  analytics_storage: "denied",
} as const;

export function applyConsent(choice: ConsentChoice) {
  if (typeof window === "undefined") return;
  const w = window as unknown as {
    gtag?: (...args: unknown[]) => void;
    ttq?: { grantConsent?: () => void; revokeConsent?: () => void };
    llLoadTikTok?: () => void;
  };
  if (choice === "declined") w.gtag?.("set", "user_data", null);
  if (choice === "declined") {
    try { localStorage.removeItem('loftloom_email_attribution'); } catch { /* unavailable */ }
    try { sessionStorage.removeItem('loftloom_editorial_source'); } catch { /* unavailable */ }
  }
  w.gtag?.("consent", "update", consentSignals(choice));
  // A visitor held at load time (Europe, or declined earlier) has no pixel yet.
  if (choice === "accepted") {
    w.llLoadTikTok?.();
    w.ttq?.grantConsent?.();
  } else w.ttq?.revokeConsent?.();
  window.dispatchEvent(new Event(CONSENT_CHANGED_EVENT));
}
